import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Star } from "lucide-react";
import NumberFlow from "@number-flow/react";
import { buttonVariants } from "./ui/button";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "./ui/carousel";
import { useMediaQuery } from "../hooks/use-media-query";
import { cn } from "../lib/utils";
import { FEATURE_ICONS, FEATURE_DESCRIPTIONS } from "../data/planFeatures";

interface PricingPlan {
  name: string;
  price: number;
  period: string;
  features: string[];
  description: string;
  buttonText: string;
  href: string;
  isPopular: boolean;
}

interface PricingProps {
  plans: PricingPlan[];
  title?: string;
  description?: string;
}

export function Pricing({
  plans,
  title = "Planos simples e transparentes",
  description = "Escolha o plano ideal para o tamanho da sua clínica.",
}: PricingProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const single = plans.length === 1;
  const [carouselApi, setCarouselApi] = useState<CarouselApi | undefined>(undefined);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => setActiveSlide(carouselApi.selectedScrollSnap());
    onSelect();
    carouselApi.on("select", onSelect);
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  const renderPlanCard = (plan: PricingPlan, index: number) => (
    <motion.div
      key={plan.name}
      initial={{ y: 16, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: isDesktop ? index * 0.08 : 0 }}
      className={
        single
          ? "flex flex-col"
          : cn(
              "rounded-2xl p-9 flex flex-col bg-card border h-full",
              plan.isPopular ? "border-primary shadow-md" : "border-border"
            )
      }
    >
      {plan.isPopular && (
        <div className={cn("inline-flex items-center gap-1.5 mb-4 text-primary", !single && "mx-auto")}>
          <Star className="h-3.5 w-3.5 fill-current" />
          <span className="text-xs font-bold tracking-wide">Mais popular</span>
        </div>
      )}
      {!plan.isPopular && !single && <div className="h-[22px] mb-4" />}

      <div className={cn("flex-1 flex flex-col", single ? "text-left" : "text-center")}>
        <p className="text-sm font-bold uppercase tracking-widest text-primary">{plan.name}</p>

        <div className={cn("mt-2 flex items-center gap-x-1", !single && "justify-center")}>
          <span className="text-5xl font-extrabold tracking-tight text-foreground">
            <NumberFlow
              value={plan.price}
              locales="pt-BR"
              format={{ style: "currency", currency: "BRL", minimumFractionDigits: 2, maximumFractionDigits: 2 }}
              transformTiming={{ duration: 500, easing: "ease-out" }}
              willChange
            />
          </span>
          <span className="text-sm font-semibold text-muted-foreground">/{plan.period}</span>
        </div>

        <p className="mt-1.5 text-sm text-muted-foreground max-w-md">{plan.description}</p>

        {single ? (
          <div className="mt-5 grid grid-cols-[auto_1fr] sm:grid-cols-[auto_220px_1fr] text-left">
            <div className="hidden sm:block pb-3 border-b border-border" />
            <div className="pb-3 pr-6 border-b border-border text-xs font-bold uppercase tracking-wide text-muted-foreground">
              Módulo
            </div>
            <div className="hidden sm:block pb-3 border-b border-border text-xs font-bold uppercase tracking-wide text-muted-foreground">
              Descrição
            </div>

            {plan.features.map((feature) => {
              const Icon = FEATURE_ICONS[feature] || Check;
              return (
                <Fragment key={feature}>
                  <div className="hidden sm:flex items-center py-4 pr-4 border-b border-border">
                    <Icon size={18} className="text-primary" />
                  </div>
                  <div className="flex items-center gap-3 py-4 pr-6 border-b border-border text-sm font-bold text-foreground">
                    <Icon size={18} className="text-primary shrink-0 sm:hidden" />
                    {feature}
                  </div>
                  <div className="hidden sm:flex items-center py-4 border-b border-border text-xs text-muted-foreground leading-relaxed">
                    {FEATURE_DESCRIPTIONS[feature]}
                  </div>
                </Fragment>
              );
            })}
          </div>
        ) : (
          // Card estreito (3 planos lado a lado) não cabe a tabela com coluna de
          // descrição — cada card mostra só ícone + nome do módulo, em linhas.
          <ul className="mt-5 divide-y divide-border text-left">
            {plan.features.map((feature) => {
              const Icon = FEATURE_ICONS[feature] || Check;
              return (
                <li key={feature} className="flex items-center gap-3 py-3 text-sm font-medium text-foreground">
                  <Icon size={16} className="text-primary shrink-0" />
                  {feature}
                </li>
              );
            })}
          </ul>
        )}

        <Link
          to={plan.href}
          className={cn(
            buttonVariants({ variant: plan.isPopular ? "default" : "outline" }),
            "h-12 rounded-xl text-sm font-semibold mt-5",
            single ? "w-auto px-10 self-start" : "w-full"
          )}
        >
          {plan.buttonText}
        </Link>
      </div>
    </motion.div>
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <div className={cn("space-y-2 mb-4", single ? "text-left" : "text-center max-w-xl mx-auto")}>
        <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">{title}</h2>
        <p className="text-muted-foreground whitespace-pre-line">{description}</p>
      </div>

      {single || isDesktop ? (
        <div className={cn("grid gap-8 md:items-center", single ? "grid-cols-1" : "grid-cols-1 md:grid-cols-3")}>
          {plans.map((plan, index) => renderPlanCard(plan, index))}
        </div>
      ) : (
        <div>
          <Carousel setApi={setCarouselApi} opts={{ align: "center", containScroll: false }}>
            <CarouselContent className="-ml-4">
              {plans.map((plan, index) => (
                <CarouselItem key={plan.name} className="basis-[84%] pl-4">
                  {renderPlanCard(plan, index)}
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          {plans.length > 1 && (
            <div className="flex items-center justify-center gap-1.5 mt-6">
              {plans.map((plan, index) => (
                <button
                  key={plan.name}
                  type="button"
                  onClick={() => carouselApi?.scrollTo(index)}
                  aria-label={`Ver plano ${plan.name}`}
                  className={cn(
                    "h-1.5 rounded-full transition-all",
                    index === activeSlide ? "w-5 bg-primary" : "w-1.5 bg-border"
                  )}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
